// VRG-679 — the Field Visits playbook and email templates, READ-ONLY, from the snapshot in
// `outreachPlaybook.generated.ts` (regenerate with apps/platform/scripts/generate-outreach-playbook.mjs).
// The canon is the umbrella repo's gtm/channels/field-visits/ at a pinned commit; this page never
// edits it.
import React, { useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Alert, Card } from '@repo/ui'

import { SelectField } from 'components'

import { OUTREACH_PLAYBOOK } from './outreachPlaybook.generated'

const P = OUTREACH_PLAYBOOK

/** Where this text came from, and how fresh it is. Printed on both tabs. */
export const PlaybookProvenance = () => (
  <Alert severity="info">
    Read-only copy of {P.provenance.repo}/gtm/channels/field-visits (the README playbook and
    templates.md) at commit {P.provenance.source}, taken {P.provenance.generatedOn}. Edit them
    there, then regenerate this copy. Not copied: {P.provenance.omitted}.
  </Alert>
)

const useMarkdownSx = () => {
  const theme = useTheme()
  return {
    '& h2': { ...theme.typography.h6, mt: 6, mb: 2 },
    '& > :first-child': { mt: 0 },
    '& p, & li': { ...theme.typography.body2, lineHeight: 1.6 },
    '& ul, & ol': { pl: 5, my: 2 },
    '& table': { borderCollapse: 'collapse', my: 3, width: '100%' },
    '& th, & td': { border: `1px solid ${theme.palette.primary[100]}`, padding: '8px 12px', textAlign: 'left', ...theme.typography.body3 },
    '& th': { fontWeight: 600 },
    '& code': { fontSize: 12 },
  }
}

export const PlaybookPanel = () => {
  const sx = useMarkdownSx()
  return (
    <Stack spacing={4}>
      <PlaybookProvenance />
      <Card>
        <Box sx={sx}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{P.playbook}</ReactMarkdown>
        </Box>
      </Card>
    </Stack>
  )
}

const EmailBlock = ({
  title,
  subject,
  subjectVariant,
  body,
  children,
}: {
  title: string
  subject: string | null
  subjectVariant?: string | null
  body: string
  children?: React.ReactNode
}) => (
  <Card title={title}>
    <Stack spacing={2} sx={{ mt: 3 }}>
      {subject && (
        <Typography variant="body2">
          <strong>Subject:</strong> {subject}
        </Typography>
      )}
      {subjectVariant && (
        <Typography variant="body3" color="text.secondary">
          {subjectVariant}
        </Typography>
      )}
      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
        {body}
      </Typography>
      {children}
    </Stack>
  </Card>
)

/** The base email with the chosen industry's opening dropped into [INDUSTRY OPENING]. */
export const TemplatesPanel = () => {
  const sx = useMarkdownSx()
  const openings = P.templates.openings
  const [industry, setIndustry] = useState<string>(openings[0]?.industry ?? '')
  const opening = openings.find((o) => o.industry === industry)
  const body = useMemo(
    () => P.templates.base.body.replace('[INDUSTRY OPENING]', opening?.text ?? '[INDUSTRY OPENING]'),
    [opening],
  )
  return (
    <Stack spacing={4}>
      <PlaybookProvenance />
      <Box sx={{ ...sx, '& p': { ...sx['& p, & li'], color: 'text.secondary' } }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{P.templates.preamble}</ReactMarkdown>
      </Box>
      <Box sx={{ maxWidth: 320 }}>
        <SelectField
          label="Industry opening"
          options={openings.map((o) => ({ value: o.industry, label: o.industry }))}
          value={industry}
          onChange={(v) => setIndustry(String(v))}
        />
      </Box>
      <EmailBlock
        title={`First email · ${industry}`}
        subject={P.templates.base.subject}
        subjectVariant={P.templates.base.subjectVariant}
        body={body}
      >
        {P.templates.base.notes && (
          // Instructions to the sender, not email text: set apart from the body.
          <Box sx={{ ...sx, pt: 2, borderTop: (t) => `1px solid ${t.palette.primary[100]}`, '& li': { ...sx['& p, & li'], color: 'text.secondary' } }}>
            <Typography variant="body3" sx={{ fontWeight: 600 }}>
              Notes for use
            </Typography>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{P.templates.base.notes}</ReactMarkdown>
          </Box>
        )}
      </EmailBlock>
      {P.templates.followUps.map((f) => (
        <EmailBlock key={f.title} title={f.title} subject={f.subject} body={f.body} />
      ))}
    </Stack>
  )
}
